<template>
  <app-page-layout
    :page-class="['msc-meals-page', { 'msc-meals-page--day-ordering': menu !== null }]"
    wide
    :show-notifications="true"
  >
    <div v-if="studentsLoaded && !hasLinkedStudents" class="msc-meals-picker non-printable">
      <card class="msc-meals-picker-card shadow">
        <h4 class="info-title mt-0">Link a student to order meals</h4>
        <p class="msc-meals-no-students__copy">
          You need at least one student linked to your account before you can pick a calendar and place orders.
        </p>
        <b-button
          class="btn btn-sm btn-fill msc-meals-no-students__cta"
          @click="goToAddStudent()"
        >
          Add a student
        </b-button>
      </card>
    </div>

    <div v-else-if="studentsLoaded && !calendar" class="msc-meals-picker non-printable">
      <card class="msc-meals-picker-card shadow">
        <h4 class="info-title mt-0">Pick a calendar</h4>
        <div v-for="(cal, index) in calendars" :key="index">
          <b-button
            @click="getCalendar(cal._id)"
            class="btn btn-sm btn-fill"
            style="width:100%;font-size:1.15em;border-radius:1em;"
          >
            {{ cal.title }}
          </b-button>
          <p class="ordercalendar-description" style="font-size:1em;font-weight:500;">{{ cal.description }}</p>
          <hr>
        </div>
      </card>
    </div>

    <div
      v-else-if="hasLinkedStudents && !isShowWeeklyOrderCalendar && !isShowMonthlyItemCalendar"
      class="msc-meals-workspace non-printable"
      :class="{ 'msc-meals-workspace--day-ordering': menu !== null }"
      :style="mealPaletteVars"
    >
      <card class="msc-meals-panel msc-meals-sidebar shadow">
        <nav class="msc-meals-toolbar">
          <button
            v-if="student"
            type="button"
            class="msc-meals-toolbar__btn msc-meals-toolbar__btn--orders"
            @click="showWeeklyOrderCalendar()"
          >
            <i class="fa fa-calendar"></i>
            <span>{{ displayName(student.firstName) }}'s Orders</span>
          </button>
          <button
            v-if="student"
            type="button"
            class="msc-meals-toolbar__btn msc-meals-toolbar__btn--monthly"
            @click="showMonthlyMealCalendar()"
          >
            <i class="fa fa-calendar-o"></i>
            <span>Monthly Calendar</span>
          </button>
          <button type="button" class="msc-meals-toolbar__btn msc-meals-toolbar__btn--nutrients" @click="nutrients()">
            <i class="fa fa-info-circle"></i>
            <span>Nutrients</span>
          </button>
          <button type="button" class="msc-meals-toolbar__btn msc-meals-toolbar__btn--calendar" @click="selectNewCalendar()">
            <i class="fa fa-calendar-check"></i>
            <span>Change Calendar</span>
          </button>
        </nav>

        <section class="msc-meals-students">
          <h3 class="msc-meals-students__title">Students</h3>
          <div class="msc-meals-students__list">
            <button
              v-for="(s, studentIndex) in students"
              :key="studentIndex"
              type="button"
              class="msc-meals-students__chip"
              :class="{ 'msc-meals-students__chip--selected': isSelectedStudent(s.studentId) }"
              @click="selectStudent(s)"
            >
              {{ displayName(s.firstName) }}
            </button>
          </div>
        </section>

        <div v-if="student !== null" class="msc-meals-calendar-wrap">
          <sweet-calendar
            :eventCategories="eventCategories"
            :events="events"
            :initialDate="calendar.calendarStartDate"
            :beginDate="calendar.calendarStartDate"
            :endDate="calendar.calendarEndDate"
            :enableControls="enableCalendarControls"
            :offDays="[]"
            cursor="not-allowed"
            eventCursor="pointer"
            :selectedDate="calendarSelectedDate"
            @click="selectCalendarDay($event)"
            ref="calendar"
          />
          <footer class="msc-meals-sidebar-footer">
            <p class="msc-meals-sidebar-footer__cutoff">Order cutoff is 7am</p>
            <div class="msc-meals-sidebar-footer__legend">
              <span class="msc-meals-sidebar-footer__legend-item msc-meals-sidebar-footer__legend-item--available">Available</span>
              <span class="msc-meals-sidebar-footer__legend-item msc-meals-sidebar-footer__legend-item--ordered">Ordered</span>
            </div>
            <p class="msc-meals-sidebar-footer__hint">Click a calendar day to order or review a previous order.</p>
            <h3 class="msc-meals-sidebar-footer__title">{{ displayName(calendar.title) || '' }}</h3>
            <p class="msc-meals-sidebar-footer__desc">{{ displayName(calendar.description) }}</p>
            <section v-if="studentOrdersLoaded" class="msc-meals-balance-summary">
              <div class="msc-meals-balance-summary__account">
                <span class="msc-meals-balance-summary__label">Account balance</span>
                <strong
                  class="msc-meals-balance-summary__value"
                  :class="{ 'msc-meals-balance-summary__value--low': accountBalance < 5 }"
                >{{ formatMoney(accountBalance) }}</strong>
              </div>

              <div class="msc-meals-balance-summary__card">
                <h4 class="msc-meals-balance-summary__heading">
                  {{ displayName(student.firstName) }} — upcoming meals
                </h4>
                <p class="msc-meals-balance-summary__note">
                  Meals ordered but not yet served. Today’s lunch drops off after noon.
                </p>
                <dl class="msc-meals-balance-summary__rows">
                  <div class="msc-meals-balance-summary__row">
                    <dt>Still to be fulfilled</dt>
                    <dd>{{ formatMoney(studentUpcomingTotal) }}</dd>
                  </div>
                  <div class="msc-meals-balance-summary__row">
                    <dt>Balance after these</dt>
                    <dd
                      :class="{ 'msc-meals-balance-summary__value--low': balanceAfterStudentUpcoming < 0 }"
                      :title="studentUpcomingTitle"
                    >{{ formatMoney(balanceAfterStudentUpcoming) }}</dd>
                  </div>
                </dl>
                <p
                  v-if="!showHouseholdUpcoming && showAutoChargeNote"
                  class="msc-meals-balance-summary__autofill-note"
                >
                  {{ autoChargeNoteText }}
                </p>
                <p
                  v-else-if="!showHouseholdUpcoming && showCardIssueNote"
                  class="msc-meals-balance-summary__autofill-note msc-meals-balance-summary__autofill-note--warn"
                >
                  {{ cardIssueNoteText }}
                </p>
              </div>

              <div
                v-if="showHouseholdUpcoming"
                class="msc-meals-balance-summary__card msc-meals-balance-summary__card--household"
              >
                <h4 class="msc-meals-balance-summary__heading">All students — upcoming</h4>
                <ul class="msc-meals-balance-summary__students">
                  <li
                    v-for="row in householdUpcomingRows"
                    :key="row.studentId"
                    :class="{ 'msc-meals-balance-summary__students-item--selected': row.isSelected }"
                  >
                    <span>{{ row.name }}</span>
                    <span>{{ formatMoney(row.upcoming) }}</span>
                  </li>
                </ul>
                <dl class="msc-meals-balance-summary__rows">
                  <div class="msc-meals-balance-summary__row">
                    <dt>Household upcoming</dt>
                    <dd>{{ formatMoney(householdUpcomingTotal) }}</dd>
                  </div>
                  <div class="msc-meals-balance-summary__row">
                    <dt>Balance after all upcoming</dt>
                    <dd
                      :class="{ 'msc-meals-balance-summary__value--low': balanceAfterHouseholdUpcoming < 0 }"
                    >{{ formatMoney(balanceAfterHouseholdUpcoming) }}</dd>
                  </div>
                </dl>
                <p
                  v-if="showAutoChargeNote"
                  class="msc-meals-balance-summary__autofill-note"
                >
                  {{ autoChargeNoteText }}
                </p>
                <p
                  v-else-if="showCardIssueNote"
                  class="msc-meals-balance-summary__autofill-note msc-meals-balance-summary__autofill-note--warn"
                >
                  {{ cardIssueNoteText }}
                </p>
              </div>
            </section>
          </footer>
        </div>
        <p v-else class="msc-meals-empty" style="min-height:8rem;font-size:1rem;">Please select a student above</p>
      </card>

      <card v-if="student" class="msc-meals-panel msc-meals-main shadow">
        <header class="msc-meals-order-header">
          <b-button class="btn btn-sm msc-meals-order-header__done" @click="doneOrdering()">All Done Ordering</b-button>
          <h3 class="msc-meals-order-header__student">
            {{ displayName(student.firstName) }} {{ displayName(student.lastName) }}
            <small>({{ displayName(student.teacher) }} / {{ student.homeRoom }})</small>
          </h3>
        </header>
        <p
          v-if="orderingStatusMessage"
          class="msc-meals-order-status"
          :class="{
            'msc-meals-order-status--warning': isZeroBalanceStatus,
            'msc-meals-order-status--clickable': isZeroBalanceStatus,
          }"
          :role="isZeroBalanceStatus ? 'link' : null"
          :tabindex="isZeroBalanceStatus ? 0 : null"
          @click="onOrderingStatusClick"
          @keydown.enter.prevent="onOrderingStatusClick"
          @keydown.space.prevent="onOrderingStatusClick"
        >
          {{ orderingStatusMessage }}
        </p>

        <div v-if="menu !== null">
          <div class="msc-meals-day-context" :style="{ '--dh-day-accent': colorForMenuDate(menu.menuDate) }">
            <div class="msc-meals-day-context__main">
              <span class="msc-meals-day-context__student">{{ displayName(student.firstName) }}</span>
              <span class="msc-meals-day-context__sep" aria-hidden="true">·</span>
              <span class="msc-meals-day-context__day">{{ orderingDayLabel }}</span>
            </div>
            <span
              class="msc-meals-day-context__balance"
              :class="{ 'msc-meals-day-context__balance--low': accountBalance < 5 || !hasMinimumCredit }"
            >
              Balance {{ formatMoney(accountBalance) }}
            </span>
          </div>

          <p v-if="hasMinimumCredit && accountBalance < 5" class="msc-meals-low-balance">Low balance — please reload your account.</p>
          <div class="msc-meals-order-grid">
            <div ref="menuPanel" class="msc-meals-menu">
              <item-selection-card
                fluid
                :class="isShadow(menuWeekday(menu.menuDate))"
                :orderDate="menuEventDateFor(menu.menuDate)"
                :color="colorForMenuDate(menu.menuDate)"
                :dayTitle="displayName(menu.title)"
                :dayDescription="displayName(menu.description)"
                :choices="choices"
                :priceLevel="student.priceLevel"
                :orderItems="activeOrderItems"
                :disabled="isOrderingDisabled || isOrderEditDisabled || !hasMinimumCredit"
                showSpineNav
                :receipt-nav-label="spineReceiptLabel"
                :exit-nav-label="spineExitLabel"
                :emphasize-receipt="spineEmphasizeReceipt"
                @add="addItem($event)"
                @remove="deleteItem($event)"
                @view-receipt="scrollToReceipt()"
                @cancel-order="handleSpineExit()"
              />
            </div>
            <div v-if="order" ref="receiptPanel" class="msc-meals-receipt">
              <receipt-card
                fluid
                :color="colorForMenuDate(menu.menuDate)"
                :orderDate="menuEventDateFor(menu.menuDate)"
                :order="order"
                title="Receipt"
                :orderLabel="hasMinimumCredit ? orderLabel : ''"
                :disabled="isOrderEditDisabled || !hasMinimumCredit"
                show-menu-back-button
                :back-button-label="receiptBackLabel"
                @delete="deleteItem($event)"
                @cancel="cancelOrder($event)"
                @order="placeOrder($event)"
                @enable="enableOrder($event)"
                @never-mind="handleReceiptNeverMind()"
              />
            </div>
          </div>

          <div
            v-if="showStickyReviewCta"
            class="msc-meals-sticky-review"
          >
            <button
              type="button"
              class="msc-meals-sticky-review__btn"
              @click="scrollToReceipt()"
            >
              Review &amp; place order
            </button>
          </div>
        </div>
        <p v-else class="msc-meals-empty">Please select a day</p>
      </card>
    </div>

    <div v-if="isShowMonthlyItemCalendar" class="msc-meals-overlay monthly-calendar non-printable" :style="mealPaletteVars">
      <div class="noprint mb-2">
        <b-button class="btn btn-success btn-sm btn-fill" @click="isShowMonthlyItemCalendar=false">Done</b-button>
      </div>
      <monthly-item-calendar
        :calendarItems="calendar"
        :config="monthlyItemCalendarConfig"
        @click="itemCalendarSelectDay($event)"
      />
    </div>

    <div v-if="isShowWeeklyOrderCalendar && !isShowMonthlyItemCalendar" class="msc-meals-overlay msc-meals-overlay--order-print" :style="mealPaletteVars">
      <monthly-order-calendar-card
        v-if="isShowWeeklyOrderCalendar"
        style="border-radius:1em;"
        :student="student"
        :orders="student.orders || []"
        :calendar="calendar"
        :viewDate="myDay"
        @click="isShowWeeklyOrderCalendar=false;selectCalendarDay($event)"
        @done="isShowWeeklyOrderCalendar=false;"
      />
    </div>
  </app-page-layout>
</template>
<script>

  import { Button, CalendarDay, Card, ChoiceSelectionCard, DailyMenuSelectionCard, FormGroupInput, TabPane, Tabs } from '@/components';
  import AppPageLayout from '@/layout/AppPageLayout.vue';
  import { MEAL_DAY_COLORS, mealDayColor, mealPaletteCssVars, MEAL_STATUS_AVAILABLE, MEAL_STATUS_ORDERED, mealDayColorFromMenuDate, menuDateWeekdayName } from '@/utils/mealDayColors';
  import ItemSelectionCard                                                                                         from "@/components/Cards/ItemSelectionCard.vue";

  import MenuItemWithExtras       from "@/components/Cards/MenuItemWithExtras.vue";
  import ReceiptCard              from "@/components/Cards/ReceiptCard.vue";
  import MonthlyOrderCalendarCard from "@/components/Cards/MonthlyOrderCalendarCard.vue";
  import MonthlyItemCalendar      from "@/components/Cards/MonthlyItemCalendar";
  import GeneralService           from "@/services/GeneralService";
  import Currency                 from "currency.js";
  import _                        from "lodash";
  import Moment                   from "moment-timezone";
  import {
    apiMenuDayKey,
    explainOrderingUnavailable,
    isAfterOrderCutoff,
    isRemainingMenuOrder,
    isTodayMenuDay,
    menuEventDateFromApi,
    orderCalendarDayKey,
    orderCalendarEventDate,
    orderingClosedForDay,
    pickerDayKey,
    pickerEventDate,
    tenantNow,
    tenantTz,
  } from '@/utils/mealDates';
  import { toTitleCase } from '@/utils/displayText';
  import { getCardExpEndDate } from '@/utils/dateFormat';
  import { Glide, GlideSlide }    from 'vue-glide-js'
  import 'vue-glide-js/dist/vue-glide.css'
  import { Calendar }             from 'vue-sweet-calendar';
  import 'vue-sweet-calendar/dist/SweetCalendar.css';

  export default {
    name      : 'app-meals-page',
    bodyClass : 'meals-page',
    components: {
      AppPageLayout,
      Card,
      Tabs,
      TabPane,
      [ Button.name ]        : Button,
      [ FormGroupInput.name ]: FormGroupInput,
      ChoiceSelectionCard,
      CalendarDay,
      DailyMenuSelectionCard,
      MenuItemWithExtras,
      ItemSelectionCard,
      MonthlyOrderCalendarCard,
      ReceiptCard,
      [ Glide.name ]         : Glide,
      [ GlideSlide.name ]    : GlideSlide,
      SweetCalendar          : Calendar,
      MonthlyItemCalendar
    },
    data() {
      return {
        slidesPerView         : 1,
        myDay                 : this.moment(),
        daySelection          : "Select a Day Above",
        selected              : [ -1, -1, -1, -1, -1, -1 ],
        dayColor              : { ...MEAL_DAY_COLORS },
        monthlyItemCalendarConfig: {
          isShowMenuDescription: true,
          isShowItemDescription: true,
          isShowItemPrice      : false,
          enablePrintCalendar  : true,
          enableNextButton     : false,
          enablePrevButton     : false,
          isShowCalenderTitle  : true
        },
        enableCalendarControls   : false,
        calendars                : [],
        calendar                 : null,
        student                  : null,
        students                 : [],
        studentsLoaded           : false,
        accountBalance           : 0,
        billingInformation       : null,
        isShowWeeklyOrderCalendar: false,
        isShowMonthlyItemCalendar: false,
        taxRate                  : 0,
        orderChanged             : false,
        dailyOrderLimit          : 99.00,           // no limit
        isShowBalance            : true,
        isOrderEditDisabled      : false,
        isOrderingDisabled       : false,
        orderLabel               : "Place Order",
        categories               : null,
        order                    : null,
        orders                   : [],
        weeklyOrders             : [],
        orderDate                : "",
        orderTotal               : 0,
        ordersRefreshKey         : 0,
        choices                  : [],
        menu                     : null,
        always                   : [],              // the always available items.
        drinks                   : [],
        condiments: [],
        eventCategories          : [
          {
            id             : 1,
            title          : "Items Available",
            textColor      : "white",
            backgroundColor: MEAL_STATUS_AVAILABLE
          },
          {
            id             : 2,
            title          : "Ordered",
            textColor      : "white",
            backgroundColor: "#a0e14f"
          },
          {
            id             : 3,
            title          : '',
            textColor      : 'white',
            backgroundColor: MEAL_STATUS_ORDERED
          },
          {
            id             : 4,
            title          : '',
            textColor      : 'white',
            backgroundColor: '#ff813a'
          },
          {
            id             : 5,
            title          : '',
            textColor      : 'white',
            backgroundColor: '#555555'
          },
          {
            id             : 6,
            title          : '',
            textColor      : 'white',
            backgroundColor: '#e5e5e5'
          }
        ],
        events                : [],
        receiptInView         : false,
      }
    },
    watch: {
      canReviewAndPlace(isReady) {
        if (isReady) {
          this.receiptInView = false;
          this.$nextTick(() => {
            this.bindReceiptVisibilityWatch();
            this.updateReceiptInView();
          });
        } else {
          this.unbindReceiptVisibilityWatch();
          this.receiptInView = false;
        }
      },
      menu() {
        this.receiptInView = false;
        this.$nextTick(() => {
          if (this.canReviewAndPlace) {
            this.bindReceiptVisibilityWatch();
            this.updateReceiptInView();
          }
        });
      },
    },
    async created() {
      await this.getCategories(); 
      await Promise.all([
        this.getBalance(),
        this.getBillingInformation(),
        this.getAlwaysItems(),
        this.getCalendars(),
        this.getStudents(),
      ]);
    },
    beforeDestroy() {
      this.unbindReceiptVisibilityWatch();
    },
    async beforeRouteLeave( to , from, next ) {
      if( !this.orderChanged ) {
        return next();
      }
      if( await this.$root.confirmYesNo('You have unsaved changes on your order, continue leaving without saving?', "warning")) {
        return next();
      }
      next(false)
    },
    computed  : {
      hasLinkedStudents() {
        return Array.isArray(this.students) && this.students.length > 0;
      },
      calendarSelectedDate() {
        const day = this.myDay ? this.moment(this.myDay) : this.moment();
        const resolved = day.isValid() ? day : this.moment();
        return new Date(resolved.year(), resolved.month(), resolved.date(), 12, 0, 0, 0);
      },
      isZeroBalanceStatus() {
        return !!(this.menu && !this.hasMinimumCredit);
      },
      studentOrdersLoaded() {
        return !!(this.student && Array.isArray(this.student.orders));
      },
      studentUpcomingOrders() {
        void this.ordersRefreshKey;
        return this.upcomingOrdersFor(this.student);
      },
      studentUpcomingTotal() {
        return _.sumBy(this.studentUpcomingOrders, (order) => this.orderAmount(order));
      },
      balanceAfterStudentUpcoming() {
        return this.accountBalance - this.studentUpcomingTotal;
      },
      studentUpcomingTitle() {
        if (!this.student) {
          return '';
        }
        const calTitle = (this.calendar && this.calendar.title) ? this.calendar.title : 'this calendar';
        const count = this.studentUpcomingOrders.length;
        const noonNote = tenantNow(this.tenantTimezone).hour() >= 12
          ? 'Today’s lunch is excluded after noon.'
          : 'Includes today through noon.';
        return `${this.displayName(this.student.firstName)}: ${count} upcoming meal${count === 1 ? '' : 's'} on “${calTitle}”. ${noonNote} Account balance is shared across all students.`;
      },
      householdUpcomingRows() {
        void this.ordersRefreshKey;
        if (!Array.isArray(this.students)) {
          return [];
        }
        return this.students
          .filter((s) => Array.isArray(s.orders))
          .map((s) => ({
            studentId : s.studentId,
            name      : this.displayName(s.firstName) || 'Student',
            upcoming  : _.sumBy(this.upcomingOrdersFor(s), (order) => this.orderAmount(order)),
            isSelected: !!(this.student && s.studentId === this.student.studentId),
          }));
      },
      householdUpcomingTotal() {
        return _.sumBy(this.householdUpcomingRows, 'upcoming');
      },
      balanceAfterHouseholdUpcoming() {
        return this.accountBalance - this.householdUpcomingTotal;
      },
      showHouseholdUpcoming() {
        return this.householdUpcomingRows.length > 1;
      },
      projectedBalanceAfterUpcoming() {
        return this.showHouseholdUpcoming
          ? this.balanceAfterHouseholdUpcoming
          : this.balanceAfterStudentUpcoming;
      },
      hasCardOnFile() {
        const cc = this.billingInformation && this.billingInformation.ccInfo;
        return !!(cc && cc.isAuthorized);
      },
      isCardOnFileExpired() {
        if (!this.hasCardOnFile) {
          return false;
        }
        const exp = this.billingInformation.ccInfo.ccExpDate;
        const endDate = getCardExpEndDate(exp);
        // Missing/unreadable expiration is treated as unusable for below-balance orders
        if (!endDate) {
          return true;
        }
        return endDate.isBefore(this.moment(), 'day');
      },
      hasChargeableCardOnFile() {
        return this.hasCardOnFile && !this.isCardOnFileExpired;
      },
      showAutoChargeNote() {
        return this.projectedBalanceAfterUpcoming < 0 && this.hasChargeableCardOnFile;
      },
      showCardIssueNote() {
        return this.projectedBalanceAfterUpcoming < 0 && !this.hasChargeableCardOnFile;
      },
      autoChargeNoteText() {
        return 'Your balance looks negative — that’s okay. We have your card on file and will automatically recharge your account with the Reload Value you have on file.';
      },
      cardIssueNoteText() {
        if (this.hasCardOnFile && this.isCardOnFileExpired) {
          return 'Your card on file looks expired. Update it on Profile before ordering beyond your available balance.';
        }
        return 'No valid card on file. Add or update a card on Profile before ordering beyond your available balance.';
      },
      hasMinimumCredit() {
        // Real account balance must have at least one cent to place more orders
        return this.moneyToNumber(this.accountBalance) >= 0.01;
      },
      hasOrderItems() {
        return !!(this.order && Array.isArray(this.order.items) && this.order.items.length > 0);
      },
      canReviewAndPlace() {
        return this.hasOrderItems
          && !this.isOrderEditDisabled
          && !this.isOrderingDisabled
          && this.hasMinimumCredit;
      },
      spineExitLabel() {
        // Discard = abandon unsaved work; Done = leave day cleanly
        return this.orderChanged ? 'Discard' : 'Done';
      },
      spineReceiptLabel() {
        // Short labels fit the narrow mobile spine without mid-word wrapping
        return this.canReviewAndPlace ? 'Review' : 'Receipt';
      },
      spineEmphasizeReceipt() {
        return this.canReviewAndPlace;
      },
      showStickyReviewCta() {
        // Hide once the receipt (with Place/Update Order) is on screen
        return this.canReviewAndPlace && !this.receiptInView;
      },
      orderingDayLabel() {
        const day = this.menu
          ? this.menuEventDateFor(this.menu.menuDate)
          : this.myDay;
        if (!day) {
          return '';
        }
        return this.moment(day).format('ddd, MMM D');
      },
      receiptBackLabel() {
        // Viewing a saved order vs editing one
        if (this.orderPlacedId(this.order) && !this.isOrderEditDisabled) {
          return 'Keep original order';
        }
        return 'Back';
      },
      mealPaletteVars() {
        return mealPaletteCssVars();
      },
      tenantTimezone() {
        return tenantTz(this.$root.account);
      },
      orderingStatusMessage() {
        if (this.isZeroBalanceStatus) {
          return 'Your account has no available balance. Tap here to reload your account.';
        }
        return explainOrderingUnavailable({
          menu                : this.menu,
          selectedDay         : this.myDay,
          tz                  : this.tenantTimezone,
          isOrderingDisabled  : this.isOrderingDisabled,
          isOrderEditDisabled : this.isOrderEditDisabled,
          order               : this.order,
        });
      },
      activeOrderItems() {
        if (!this.order || !Array.isArray(this.order.items)) {
          return [];
        }
        return this.order.items;
      },
    },
    methods   : {
      mealDayColor,
      menuWeekday(menuDate) {
        return menuDateWeekdayName(menuDate, this.tenantTimezone);
      },
      colorForMenuDate(menuDate) {
        return mealDayColorFromMenuDate(menuDate, this.tenantTimezone);
      },
      menuEventDateFor(menuDate) {
        return menuEventDateFromApi(menuDate, this.tenantTimezone);
      },
      moment(d) {
        return Moment(d);
      },
      isShadow(d) {
        if(d === this.daySelection) {
          return "shadow";
        }
      },
      async showMonthlyMealCalendar() {
        // await this.$root.alert( "Coming Soon" );
        this.isShowMonthlyItemCalendar = true;
      },
      qtyOnOrder( mi ) {
console.log( mi );
        return mi.menuItemId;
      },
      isSelectedStudent(studentId) {
        if(!this.student) {
          return false;
        }

        if(this.student.studentId === studentId) {
          return true;
        }
        return false;
      },
      nutrients() {
        window.open(`/doc/ingredients-${this.$root.account.tenant.domainName}.pdf`, "_blank");
      },
      itemCalendarSelectDay( d ) {
        this.isShowMonthlyItemCalendar = false;
        this.selectCalendarDay(pickerEventDate(d.year, d.month, d.day));
      },
      async showWeeklyOrderCalendar() {
        if (!this.student || !this.calendar) {
          return;
        }
        if (!Array.isArray(this.student.orders)) {
          await this.getOrders(this.student);
        }
        if (!Array.isArray(this.student.orders)) {
          this.$set(this.student, 'orders', []);
        }
        this.isShowWeeklyOrderCalendar = true;
      },
      trySetOrderDisable(status) {
        this.isOrderEditDisabled = status;
      },
      newTransaction() {

        this.order = {
          header    : [],
          trailer   : [],
          subTotal  : 0,
          tax       : 0,
          total     : 0,
          totalItems: 0,
          priceLevel: 0,
          items     : []
        };

        this.orderChanged = false;
        this.trySetOrderDisable(false);
        this.calculateOrderTotal();
      },
      calculateOrderTotal() {
        let t              = this;
        t.order.subTotal   = _.sumBy(t.order.items, function(item) {
          return item.price * item.qty;
        })
        t.order.totalItems = _.sumBy(t.order.items, function(item) {
          return item.qty;
        })
        t.order.tax        = t.currency(t.order.subTotal).multiply(t.taxRate);
        t.order.total      = t.currency(t.order.subTotal).add(t.order.tax);
      },
      async getWeeklyOrders() {

        try {
          let response = await GeneralService.getWeeklyOrders( this.moment(this.myDay).format( "YYYY-MM-DD" ) , this.student.studentId );
          this.weeklyOrders = response.data;
        } catch( error ) {
          console.log( error.message );
        }
      },
      async cancelOrder(order) {
        if(order._id) {
          if( new Date(order.orderDate).getTime() <= new Date().getTime()) {
            return await this.$root.alert("Cannot Remove A Sent Past Order");
          }

          if( await this.$root.confirmYesNo("Cancel This EXISTING Order?")) {
            await this.removeOrder(order);
            this.newTransaction();
            this.selectCalendarDay(this.myDay);
            this.notify("success", "Your Order For This Day Has Been Canceled");
          }
        } else {
          if(order.items.length > 0) {
            this.newTransaction();
            this.notify("success", "Starting Over...");
          }
        }
      },
      async replaceOrder(order) {
        if(!order._id) {
          return await this.$root.alert("This order can not be updated");
        }
        await this.updateOrder(order);
        this.newTransaction();
        this.notify("success", "Your Order Has Been Updated");
      },
      enableOrder(order) {
        if (!this.hasMinimumCredit) {
          this.$root.alert(
            'Your account has no available balance. Please reload on Profile before changing orders.'
          );
          return;
        }
        if(!orderingClosedForDay(order.orderDate, this.tenantTimezone)) {
          this.trySetOrderDisable(false);
        }
        if (this.isNarrowViewport()) {
          this.$nextTick(() => this.scrollToMenu());
        }
      },
      async placeOrder(order) {

        if(this.order.items.length === 0) {
          return;
        }

        if (!this.hasMinimumCredit) {
          await this.$root.alert(
            'Your account has no available balance. Please reload your account — I’ll take you there now.'
          );
          this.orderChanged = false;
          this.$router.push('/app/profile');
          return;
        }

        if(!this.hasDrink()) {
          if(! await this.$root.confirmYesNo("No drink on this order, continue anyway? ")) {
            return;
          }
        }

        this.calculateOrderTotal();

        const payload = _.cloneDeep(order);
        payload.accountNumber  = this.student.accountNumber;
        payload.badgeNumber    = this.student.badgeNumber;
        payload.alias          = this.student.firstName + " " + this.student.lastName;
        payload.grade          = this.student.grade;
        payload.teacher        = this.student.teacher;
        payload.homeRoom       = this.student.homeRoom;
        payload.calendarNumber = this.calendar.calendarNumber;
        if (this.menu && this.menu.menuDate) {
          payload.orderDate = this.menu.menuDate;
        }

        // Block orders that would push projected balance negative when card can't be charged.
        if (!this.hasChargeableCardOnFile) {
          const projected = this.projectedBalanceAfterPlacing(payload);
          if (projected < 0) {
            const reason = this.hasCardOnFile && this.isCardOnFileExpired
              ? 'Your card on file appears to be expired.'
              : 'You do not have a valid card on file.';
            await this.$root.alert(
              `${reason} This order would leave your balance after upcoming meals at ${this.formatMoney(projected)}. ` +
              `Please update your card or reload your account on Profile before placing it.`
            );
            this.orderChanged = false;
            this.$router.push('/app/profile');
            return;
          }
        }

        let saved = false;
        const isUpdate = !!order._id;
        if (isUpdate) {
          saved = await this.updateOrder(payload);
        } else {
          saved = await this.postOrder(payload);
        }

        if (!saved) {
          return;
        }

        const studentName = this.displayName(this.student && this.student.firstName) || 'your student';
        const dayLabel = this.moment(
          payload.orderDate || (this.menu && this.menu.menuDate) || this.myDay
        ).format('ddd, MMM D');
        this.notify(
          'success',
          isUpdate
            ? `Updated order for ${studentName} — ${dayLabel}`
            : `Ordered for ${studentName} — ${dayLabel}`
        );

        this.syncStudentOrderSnapshot(payload);
        this.newTransaction();

        // On mobile, return to the calendar so the next day choice is clear.
        // Desktop keeps the day open so the saved receipt stays visible beside the menu.
        if (this.isNarrowViewport()) {
          this.exitToCalendar();
        } else {
          this.selectCalendarDay(Moment(payload.orderDate));
        }
      },
      addItem(item, usePriceLevel) {
        let t = this;

        if(this.isOrderEditDisabled === true || !this.hasMinimumCredit) {
          return;
        }

        if(!this.order) {
          this.newTransaction();
        }

        this.orderChanged = true;
        let existing      = _.find(this.order.items, {itemNumber: item.itemNumber});

        this.order.orderDate = item.orderDate;      // order has date of last item added.

        if(existing) {
          // todo: see what our max item is.
          if(existing.qty < item.maxChoice) {
            existing.qty++;
          }
        } else {
          this.order.items.push(item);
          // console.log( item );
          this.order.items = _.sortBy(this.order.items, "itemNumber");
        }

        this.calculateOrderTotal();

      },
      deleteItem(itemNumber) {
        if (this.isOrderEditDisabled) {
          return;
        }
        if(this.order.items.length === 0) { return; }
        let existing = _.find(this.order.items, {itemNumber: itemNumber});
        if(existing) {
          this.orderChanged = true;
          this.$emit("remove", itemNumber);
          existing.qty--;
          if(existing.qty <= 0) {
            _.remove(this.order.items, function(item) {
              return item.itemNumber === itemNumber;
            })
          }
        }
        this.calculateOrderTotal();
      },
      nextDay() {
        this.myDay = this.moment(this.myDay).add(1, 'day');
      },
      async selectStudent(student) {
        if(this.isOrderEditDisabled === false && this.orderChanged === true) {
          await this.$root.alert("Complete Your Order Before Changing Students");
          return;
        }

        this.student = student;

        this.newTransaction();
        this.menu = null;
        this.choices = [];
        this.isOrderingDisabled = false;
        this.refreshOrderLabel();

        if (this.calendar) {
          await this.getOrders(this.student);
          this.buildEvents(this.calendar);
        }

        if (typeof window !== 'undefined' && window.scrollTo) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      async doneOrdering() {
        if(this.isOrder()) {
          if(!this.isOrderEditDisabled) {
            await this.$root.alert("Complete The Current Order First");
            return;
          }
        }
        this.$router.push('/app/main')
      },
      selectMenu(menu) {
// console.log( "selectMenu()" , menu );
        this.menu    = menu;
        this.choices = [];
        this.choices.push({title: "Meal Selections", usePriceLevel: true, items: menu.choices.filter((i) => { return i.itemNumber > 0})});
        if( this.condiments ) {
         this.choices.push({title: "Condiments", usePriceLevel: false, items: this.condiments.filter((i) => { return i.itemNumber > 0})}); 
        }
        this.choices.push({title: "Drink Selections", usePriceLevel: false, items: this.drinks.filter((i) => { return i.itemNumber > 0})});

        let title = "A La Carte";

        if( this.categories ) {
          let x = _.find( this.categories , { category: "always" });
          if( x ) {
            title = x.title || "A La Carte";
          }
        }

        this.choices.push({title: title, usePriceLevel: false, items: this.always.filter((i) => { return i.itemNumber > 0})});

        
      },
      async selectCalendarDay(d) {
        if(this.isOrder()) {
          if(!this.isOrderEditDisabled) {
            await this.$root.alert("Complete The Current Order First");
            return;
          }
        }

        this.myDay = d;
        const tz = this.tenantTimezone;
        const selectedKey = pickerDayKey(d, tz);

        let menu;
        try {
          menu = _.find(this.calendar.menu, function(m) {
            return apiMenuDayKey(m.menuDate, tz) === selectedKey;
          });
        } catch(error) {
          menu = null;
          console.error(error);
        }
        if(menu && menu.choices.length > 0) {
          if(this.isOrder()) {
            this.newTransaction();
          }
          this.selectMenu(menu);

          if(this.student) {
            if(this.student.orders) {
              let order = _.find(this.student.orders, function(order) {
                return orderCalendarDayKey(order.orderDate, tz) === selectedKey;
              });
              this.isOrderingDisabled = false;
              if(order) {
                this.trySetOrderDisable(true);
                this.order = _.cloneDeep(order);
                this.calculateOrderTotal();
                this.$nextTick(() => {
                  if (this.isNarrowViewport()) {
                    this.scrollToReceipt();
                  }
                });
              } else {
                this.trySetOrderDisable(false);
                this.isOrderingDisabled = false;
              }

              if (orderingClosedForDay(d, tz)) {
                this.isOrderingDisabled = true;
              }
            }
          }
        } else {
          this.menu = null
          this.trySetOrderDisable(true);
          this.isOrderingDisabled = true;
        }

        if (orderingClosedForDay(d, tz)) {
          if (isTodayMenuDay(d, tz) && !isAfterOrderCutoff(d, tz)) {
            this.refreshOrderLabel();
          } else {
            this.orderLabel = "";
            this.isOrderEditDisabled = true;
          }
        } else {
          this.refreshOrderLabel();
        }
      },
      refreshOrderLabel() {
        // Existing saved orders say Update; new drafts say Place Order
        if (this.orderPlacedId(this.order)) {
          this.orderLabel = 'Update Order';
        } else {
          this.orderLabel = 'Place Order';
        }
      },
      hydrateOrderCalendar(calendar) {

        this.calendar               = calendar;
        this.enableCalendarControls = false;

        if(!calendar) {
          return;
        }

        try {

          this.buildEvents(calendar);

          if(new Date(calendar.calendarStartDate).getMonth() !== new Date(calendar.calendarEndDate).getMonth()) {
            this.enableCalendarControls = true;
          }

        } catch(error) {
          // todo: tell the user something about this issue.
          console.error(error);
        }
      },
      buildEvents(calendar) {
        this.events = [];

        if(!calendar) {
          return;
        }

        const tz = this.tenantTimezone;
        const todayKey = tenantNow(tz).format('YYYY-MM-DD');

        if(calendar.menu) {
          for(let m of calendar.menu){
            let cat;
            const dayKey = apiMenuDayKey(m.menuDate, tz);
            const eventDate = menuEventDateFromApi(m.menuDate, tz);
            const closedForOrdering = dayKey < todayKey || isAfterOrderCutoff(eventDate, tz);
            if(closedForOrdering) {
              cat = 6;
            } else {
              cat = m.choices.length > 0 ? 1 : 0;
            }
            let event = {
              title     : m.title || "",
              start     : eventDate,
              end       : eventDate,
              repeat    : "never",
              categoryId: cat
            };
            this.events.push(event);
          }
        }

        if(this.student) {
          if(this.student.orders) {
            for(let m of this.student.orders){
              const orderDayKey = orderCalendarDayKey(m.orderDate, tz);
              _.remove(this.events, (e) => pickerDayKey(e.start, tz) === orderDayKey);
              const eventDate = orderCalendarEventDate(m.orderDate, tz);
              let event = {title: "order", start: eventDate, end: eventDate, repeat: "never", categoryId: 3};
              this.events.push(event);
            }
          }
        }

      },
      currency(c) {
        return Currency(c);
      },
      formatMoney(amount) {
        return Currency(amount).format();
      },
      displayName(value) {
        return toTitleCase(value);
      },
      scrollToReceipt() {
        const panel = this.$refs.receiptPanel;
        if (panel && panel.scrollIntoView) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
      scrollToMenu() {
        const panel = this.$refs.menuPanel;
        if (panel && panel.scrollIntoView) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
      bindReceiptVisibilityWatch() {
        if (this._receiptScrollBound) {
          return;
        }
        if (typeof window === 'undefined' || !window.addEventListener) {
          return;
        }
        this._onReceiptScroll = () => this.scheduleReceiptInViewUpdate();
        window.addEventListener('scroll', this._onReceiptScroll, { passive: true });
        window.addEventListener('resize', this._onReceiptScroll, { passive: true });
        this._receiptScrollBound = true;
      },
      unbindReceiptVisibilityWatch() {
        if (this._receiptRafId != null && typeof window !== 'undefined' && window.cancelAnimationFrame) {
          window.cancelAnimationFrame(this._receiptRafId);
        }
        this._receiptRafId = null;
        this._receiptRafPending = false;
        if (!this._receiptScrollBound) {
          return;
        }
        if (typeof window !== 'undefined' && window.removeEventListener && this._onReceiptScroll) {
          window.removeEventListener('scroll', this._onReceiptScroll);
          window.removeEventListener('resize', this._onReceiptScroll);
        }
        this._onReceiptScroll = null;
        this._receiptScrollBound = false;
      },
      scheduleReceiptInViewUpdate() {
        if (this._receiptRafPending) {
          return;
        }
        this._receiptRafPending = true;
        const run = () => {
          this._receiptRafPending = false;
          this._receiptRafId = null;
          this.updateReceiptInView();
        };
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          this._receiptRafId = window.requestAnimationFrame(run);
        } else {
          run();
        }
      },
      updateReceiptInView() {
        if (!this.canReviewAndPlace) {
          this.receiptInView = false;
          return;
        }
        const panel = this.$refs.receiptPanel;
        if (!panel || typeof panel.getBoundingClientRect !== 'function') {
          this.receiptInView = false;
          return;
        }
        const rect = panel.getBoundingClientRect();
        const viewportH = window.innerHeight || 0;
        // Only hide sticky once the receipt has meaningfully entered the viewport
        // (not merely because it exists further down the page).
        this.receiptInView = rect.top < viewportH * 0.65 && rect.bottom > 80;
      },
      restoreSavedOrderForSelectedDay() {
        if (!this.student || !this.student.orders) {
          this.newTransaction();
          return;
        }

        const tz = this.tenantTimezone;
        const selectedKey = pickerDayKey(this.myDay, tz);
        const saved = _.find(this.student.orders, (o) => orderCalendarDayKey(o.orderDate, tz) === selectedKey);

        if (saved) {
          this.order = _.cloneDeep(saved);
          this.calculateOrderTotal();
        } else {
          this.newTransaction();
        }
      },
      exitToCalendar() {
        this.newTransaction();
        this.menu = null;
        this.isOrderingDisabled = false;
        this.orderLabel = 'Place Order';
        this.orderChanged = false;

        if (typeof window !== 'undefined' && window.scrollTo) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      orderPlacedId(order) {
        if (!order) {
          return null;
        }
        return order._id || order.id || null;
      },
      async handleReceiptNeverMind() {
        if (!this.orderPlacedId(this.order)) {
          return;
        }

        // Viewing a placed order — nothing to undo, just leave the day.
        if (this.isOrderEditDisabled) {
          this.exitToCalendar();
          return;
        }

        // Editing a placed order — revert to the saved order and stay on this day.
        if (this.orderChanged) {
          if (!await this.$root.confirmYesNo('Discard your changes and keep the original order?')) {
            return;
          }
        }

        this.restoreSavedOrderForSelectedDay();
        this.orderChanged = false;
        this.trySetOrderDisable(true);
      },
      isNarrowViewport() {
        if (typeof window === 'undefined' || !window.matchMedia) {
          return false;
        }
        return window.matchMedia('(max-width: 1199px)').matches;
      },
      async handleSpineExit() {
        // Done with items ready to place → jump to receipt / Place Order (don't leave the day).
        if (!this.orderChanged && this.canReviewAndPlace) {
          this.scrollToReceipt();
          return;
        }
        await this.abortDayOrdering();
      },
      async abortDayOrdering() {
        // Placed order, view-only: same as receipt Never mind — no confirmation needed.
        if (this.orderPlacedId(this.order) && this.isOrderEditDisabled) {
          this.exitToCalendar();
          return;
        }

        const hasUnsavedWork = this.orderChanged
          || (this.isOrder() && !this.isOrderEditDisabled);

        if (hasUnsavedWork) {
          const message = this.orderPlacedId(this.order)
            ? 'Discard your changes and return to the calendar?'
            : 'Discard this order and return to the calendar?';
          if (!await this.$root.confirmYesNo(message)) {
            return;
          }
        }

        this.exitToCalendar();
      },
      moneyToNumber(amount) {
        if (amount == null || amount === '') {
          return 0;
        }
        if (typeof amount === 'number') {
          return amount;
        }
        if (typeof amount === 'string') {
          return parseFloat(amount) || 0;
        }
        if (typeof amount === 'object') {
          if (typeof amount.format === 'function') {
            return parseFloat(amount.format().replace(/[^0-9.-]+/g, '')) || 0;
          }
          if (amount.value != null) {
            const v = Number(amount.value);
            if (amount.precision != null) {
              return v / Math.pow(10, amount.precision);
            }
            // currency.js cents (900 = $9) vs API plain dollars in value (9 = $9)
            if (Number.isInteger(v) && v >= 100) {
              return v / 100;
            }
            return v;
          }
        }
        return parseFloat(amount) || 0;
      },
      orderAmount(order) {
        if (!order) {
          return 0;
        }
        if (order.items && order.items.length) {
          return _.sumBy(order.items, (item) => {
            return this.moneyToNumber(Currency(item.price).multiply(item.qty || 1));
          });
        }
        if (order.total != null && order.total !== '') {
          return this.moneyToNumber(order.total);
        }
        if (order.subTotal != null) {
          return this.moneyToNumber(order.subTotal) + this.moneyToNumber(order.tax);
        }
        return 0;
      },
      upcomingOrdersFor(student) {
        if (!student || !Array.isArray(student.orders)) {
          return [];
        }
        const tz = this.tenantTimezone;
        return student.orders.filter((order) => isRemainingMenuOrder(order.orderDate, tz));
      },
      syncStudentOrderSnapshot(order) {
        if (!this.student || !order) {
          return;
        }
        const tz = this.tenantTimezone;
        const snapshot = _.cloneDeep(order);
        if (this.menu && this.menu.menuDate) {
          snapshot.orderDate = this.menu.menuDate;
        }
        const orderKey = orderCalendarDayKey(snapshot.orderDate, tz);
        const orders = Array.isArray(this.student.orders) ? this.student.orders.slice() : [];
        const idx = _.findIndex(
          orders,
          (existing) => orderCalendarDayKey(existing.orderDate, tz) === orderKey
        );
        if (idx >= 0) {
          orders.splice(idx, 1, snapshot);
        } else {
          orders.push(snapshot);
        }
        this.$set(this.student, 'orders', orders);
        this.buildEvents(this.calendar);
        this.ordersRefreshKey += 1;
      },
      removeStudentOrderSnapshot(order) {
        if (!this.student || !order || !Array.isArray(this.student.orders)) {
          return;
        }
        const tz = this.tenantTimezone;
        const orderKey = orderCalendarDayKey(order.orderDate, tz);
        const orders = this.student.orders.filter(
          (existing) => orderCalendarDayKey(existing.orderDate, tz) !== orderKey
        );
        this.$set(this.student, 'orders', orders);
        this.buildEvents(this.calendar);
        this.ordersRefreshKey += 1;
      },
      isOrder() {
        if(!this.order) {      // no object = no order.
          return false;
        }
        if(this.order.items.length === 0) { // no items, no order
          return false;
        }
        return true;
      },
      hasDrink() {
        if(!this.order) {
          return false;
        }
        if(_.find(this.order.items, {itemCategory: "drinks"})) {
          return true;
        }
        return false;
      },
      hasOrder(student, orderDate) {

        if(!student) {
          return false;
        }
        if(!student.orders) {
          return false;
        }

        let order = _.find(student.orders, {orderDate: orderDate});

        if(order) {
          return true;
        }

        return false;

      },
      async getBalance() {

        try {
          let response        = await GeneralService.getBalance();
          this.accountBalance = response.data.balance;

// console.log( "balance = " , response.data );

        } catch(error) {
          this.notify("danger", error.message);      // todo: update with a better message.
        }
      },
      async getBillingInformation() {
        try {
          const response = await GeneralService.getBillingInformation();
          this.billingInformation = (response.data && response.data.billing) || null;
        } catch (error) {
          console.error(error);
          this.billingInformation = null;
        }
      },
      /**
       * Account balance minus household upcoming, as if `payload` were the saved order for the selected day.
       */
      projectedBalanceAfterPlacing(payload) {
        const tz = this.tenantTimezone;
        const orderDate = (payload && payload.orderDate) || (this.menu && this.menu.menuDate) || this.myDay;
        const dayKey = orderCalendarDayKey(orderDate, tz);
        const selectedStudentId = this.student ? this.student.studentId : null;
        const newAmount = isRemainingMenuOrder(orderDate, tz) ? this.orderAmount(payload) : 0;

        let upcomingTotal = 0;
        (this.students || []).forEach((student) => {
          const orders = Array.isArray(student.orders) ? student.orders : [];
          orders.forEach((existing) => {
            if (!isRemainingMenuOrder(existing.orderDate, tz)) {
              return;
            }
            const sameStudent = student.studentId === selectedStudentId;
            const sameDay = orderCalendarDayKey(existing.orderDate, tz) === dayKey;
            if (sameStudent && sameDay) {
              return; // replaced by the order being placed
            }
            upcomingTotal += this.orderAmount(existing);
          });
        });

        upcomingTotal += newAmount;
        return this.accountBalance - upcomingTotal;
      },
      async getAlwaysItems() {
        let d,a,c;
        try {
          [ d,a,c ] = await Promise.all([ 
              GeneralService.getMenuItems("drinks"),
              GeneralService.getMenuItems("always"),
              GeneralService.getMenuItems("condiments")
          ])
        } catch( error ) {
          console.log( error.message );
        }
        this.drinks     = d.data;
        this.always     = a.data;
        this.condiments = c.data;
      },
      async getCategories() {
        try {
          let response = await GeneralService.getCategories();
          this.categories = response.data;
        } catch(error) {
          console.log( error.message );
        }
      },
      async getMenuItems(category) {
        try {
          let response = await GeneralService.getMenuItems(category);     // comma sep list (or empty for default)
          return response.data;

        } catch(error) {
          this.notify("danger", error.message);      // todo: update with a better message.
        }
      },
      goToAddStudent() {
        this.$router.push('/app/profile/#addStudents');
      },
      goToReload() {
        this.$router.push('/app/profile');
      },
      onOrderingStatusClick() {
        if (this.isZeroBalanceStatus) {
          this.goToReload();
        }
      },
      async selectNewCalendar() {
        this.calendar = null;
        this.students.forEach(function(student) {
          student.orders = null;
        })
      },
      async getCalendar(id) {
        try {
          let response = await GeneralService.getOrderCalendar(id);
          this.hydrateOrderCalendar(response.data);

          let day = this.myDay; // Moment();

          if(day < response.data.calendarStartDate) {
            day = response.data.calendarStartDate
          } else if(day > response.data.calendarEndDate) {
            day = response.data.calendarEndDate
          }

          if(this.students.length > 0) {
            await this.selectStudent(this.students[ 0 ]);
            // Load siblings so the household upcoming summary can include everyone
            await this.loadOrdersForOtherStudents();
          }

          this.selectCalendarDay(day);

        } catch(error) {
          this.notify("danger", `getCalendar(${id}) Unable To Retrieve Calendar.` + error.message);
        }
      },
      async getCalendars() {
        try {
          let response   = await GeneralService.getOrderCalendars();
          this.calendars = response.data;
        } catch(error) {
          console.error(error);
          this.notify("danger", "getCalendars() Unable To Retrieve A Order Calendar. " + error.message);
        }
      },
      async getOrders(student, options = {}) {
        const refreshCalendar = options.refreshCalendar !== false;

        if(!this.calendar)     // we get pinged on page load.
        {
          return;
        }

        try {
          let response   = await GeneralService.getOrders(this.calendar.calendarNumber, student.badgeNumber);
          const payload  = response.data;
          const orders   = Array.isArray(payload)
            ? payload
            : (payload && Array.isArray(payload.orders) ? payload.orders : []);
          this.$set(student, 'orders', orders);

          if (
            refreshCalendar
            && this.student
            && student
            && student.studentId === this.student.studentId
          ) {
            this.hydrateOrderCalendar(this.calendar);
          }
          this.ordersRefreshKey += 1;

        } catch(error) {
          console.error(error);
          this.notify("danger", "getOrders() Unable To Retrieve Student Orders " + error.message);
        }
      },
      async loadOrdersForOtherStudents() {
        if (!this.calendar || !Array.isArray(this.students) || this.students.length < 2) {
          return;
        }
        const selectedId = this.student ? this.student.studentId : null;
        const others = this.students.filter((s) => s && s.studentId !== selectedId);
        if (!others.length) {
          return;
        }
        await Promise.all(
          others.map((s) => this.getOrders(s, { refreshCalendar: false }))
        );
      },
      async postOrder(order) {

        try {
          await GeneralService.postOrder(order);
          await this.getOrders(this.student);
          return true;
        } catch(error) {
          console.error(error);
          if( error.response ) {
            console.log( error.response );
            if( error.response.data  ) {
              if( error.response.status === 409 ) {
                await this.$root.alert( "It appears an order already exists for this day.  Please make sure you do not have multiple browsers open trying to do the same task.  THIS order will be ignored and your previous order remains. I will attempt to retrieve the other orders when you press OK.  You can also log out and try again if this issue persists." );
                await this.getOrders(this.student);
              } else {
                this.notify( "danger" , error.response.data.message );
              }
            } else {
             his.notify("danger", error.message ); 
            }
          } else {
            this.notify("danger", "postOrder() Unable To Save This Order " + error.message);
          }
          return false;
        }
      },
      async updateOrder(order) {
        try {
          await GeneralService.updateOrder(order);
          await this.getOrders(this.student);
          return true;
        } catch(error) {
          console.error(error);
          this.notify("danger", "updateOrder() Unable To Save This Order " + error.message);
          return false;
        }
      },
      async removeOrder(order) {
        try {
          await GeneralService.deleteOrder(order._id);
          this.removeStudentOrderSnapshot(order);
          await this.getOrders(this.student);
          return true;
        } catch(error) {
          console.error(error);
          this.notify("danger", "updateOrder() Unable To Save This Order " + error.message);
          return false;
        }
      },
      async getStudents() {
        try {
          let response  = await GeneralService.getStudents();
          this.students = response.data.students;
        } catch(error) {
          // todo: tell the user about this issue...
          console.error(error);
        } finally {
          this.studentsLoaded = true;
        }
      },
      makeCalendar(d) {

      },
      notify(type, message) {        // fixme: refactor to move this to a component or root
        let t    = this;
        let icon = "fa fa-info-circle";
        
        // if(type === "danger") {
        //   icon = "fa fa-error";
        // } else if(type === "success") {
        //   icon = "fa fa-ok";
        // } else if(type === "error") {
        //   type = "danger";
        //   icon = "fa fa-danger";
        // } else {
        //   icon = "fa fa-warning";
        // }
        
        t.$notify({
                    message        : message,
                    icon           : icon,
                    horizontalAlign: 'center',  // right | center | left
                    verticalAlign  : 'bottom',  // top | bottom
                    type           : type       // info | warning | danger | success | primary
                  })
      }
      
    }
  }
</script>
<style scoped lang="scss">

  .ordercalendar-description {
    /*font-family: "Arial";*/
    white-space:pre-line;
    text-align:center;
    font-weight:300;
    line-height:1.25em;
  }

  .monthly-calendar {
    padding:.5em;
  }

  /*body { margin: 0; }*/

table { width: 100%; box-sizing: border-box; box-shadow: inset 0px 0px 0px 1px rgba(0,0,0,0.1); font-family: 'Ubuntu'; /*border-collapse: collapse;*/ border-spacing: 0; background-color: #333333; }

th, td { border: 1px solid rgba(255,255,255,0.1); box-sizing: border-box; }

th { text-transform: uppercase; font-size:10px; font-weight:700; padding: 10px 0; color: rgba(255,255,255,0.5); background-color: #292929; letter-spacing: 1px; }

td { width: 14.285%; transition: all 0.3s; font-size: 14px; color: rgba(255,255,255,0.6); font-weight: 400; font-size: 14px; padding: 1.5% 1.5% 5%; vertical-align: initial; padding: 1.5% 0 ; height: 75px; }

.day:hover { background-color: rgba(0,0,0,0.2); cursor:pointer; }

.today { color: #FFF; background-color: rgba(0,0,0,.25) !important; }

span.number { margin-left: 10% }

span.event { height: 4px; background-color: rgba(0,0,0,.3); display: block; margin: 5px 10%; border-radius: 2px; background-color: #91c33b; }

span.event-multiday { margin: 5px -2px; border-radius: 0; }
span.event-multiday-start { margin-right: -4px;  }
span.event-multiday-finish { margin-left: -4px;  }

span.event-ghost { background-color:transparent; }


  /*******************************************************************/


  @media print {


    /** { margin: 0 !important; padding: 0 !important; }*/
    /*#controls, .footer, .footerarea{ display: none; }*/
    /*html, body {*/
    /*  !*changing width to 100% causes huge overflow and wrap*!*/
    /*  height:100%;*/
    /*  overflow: hidden;*/
    /*  background: #FFF;*/
    /*  font-size: 9.5pt;*/
    /*}*/

    /*.template { width: auto; left:0; top:0; }*/
    /*img { width:100%; }*/
    /*li { margin: 0 0 10px 20px !important;}*/

    /****************************************************/

    .logo {
      width: 300px !important;
      max-width: 300px !important;
    }

    .non-printable, noprint, #nav, footer, #footer, .footer {
      display: none;
    }

    .container {
      width: 100%;
      max-width: 100%;
    }

    .col-bg.width-20 {
      max-width: 20%;
      width: 20%;
      flex: 0 0 20%;
      border: 1px solid rgba(0, 0, 0, .5);;
      border-right: none;
    }

    .col-bg.width-20:last-child {
      border-right: 1px solid rgba(0, 0, 0, .5);;
    }

    .week-title {
      color: #333 !important;
      border: none;
      border-bottom: 1px solid rgba(0, 0, 0, .5);
    }

    .week-content .item-detail {
      border-color: rgba(0, 0, 0, .5) !important;
    }
  }

  .logo {
    max-width: 500px;
  }

  .student-info {
    font-size: 20px;
    text-align: left;
    @media only screen and (max-width: 574px) {
      margin-top: 20px;
    }
  }

  .btn-outline-primary {
    color: #1d9a86;
    border-color: #1d9a86;
    min-width: 130px;
  }

  .btn-outline-primary:not(:disabled):not(.disabled):active,
  .btn-outline-primary:hover,
  .btn-outline-primary:active {
    background-color: #1d9a86;
    border-color: #1d9a86;

  }

  .btn-outline-primary:focus, .btn-outline-primary.focus {
    box-shadow: 0 0 0 0.2rem rgba(29, 154, 134, 0.5) !important;
  }

</style>
